import React, { useContext } from "react";
import { AppBar, Toolbar, Button, Box, Typography, Container } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../providers/AuthProvider";
import { getNavRoutes } from "../Routes";

interface AppShellProps {
  children: React.ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useContext(AuthContext);
  const navRoutes = getNavRoutes();

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    setUser(null);
    navigate("/login", { replace: true });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <Typography variant="h6" sx={{ mr: 4, fontWeight: 700 }} data-cy="app-title">
            {t("nav.title")}
          </Typography>
          <Box sx={{ flexGrow: 1, display: "flex", gap: 1 }}>
            {navRoutes.map((route) => (
              <Button
                key={route.path}
                color="inherit"
                onClick={() => navigate(route.path)}
                data-cy={`nav-${route.path === "/" ? "overview" : route.path.replace("/", "")}`}
                sx={{
                  fontWeight: isActive(route.path) ? 700 : 400,
                  borderBottom: isActive(route.path) ? "2px solid" : "2px solid transparent",
                  borderRadius: 0,
                }}
              >
                {t(route.nameKey)}
              </Button>
            ))}
          </Box>
          <Button color="inherit" onClick={handleLogout} data-cy="logout">
            {t("nav.logout")}
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {children}
      </Container>
    </Box>
  );
};

export default AppShell;
